import React from 'react';

import { useAppContext } from 'src/components/app-context';

import {
  BodySmallText,
  CaptionText,
  FontEmphasis,
} from '@prisjakt/ui/text/variants';
import { Currency } from 'src/components/currency';
import { ProductName } from '../product-name';
import { DealBadge } from '../deal-badge';
import { Price } from '../price';
import Rating from 'src/components/rating';

import { MainContent, RowWrapper, PriceTextWrapper, Tag } from './styles';

import type { BaseProps } from '../../types';
import { MediumUp, MobileOnly } from 'src/ui/block';

type Props = Pick<
  BaseProps,
  | 'name'
  | 'highlightedName'
  | 'rank'
  | 'rating'
  | 'reviewsCount'
  | 'diffPercentage'
  | 'price'
  | 'priceInclShipping'
  | 'properties'
  | 'priceCount'
  | 'showFromPrice'
  | 'store'
> & {
  showClickout?: boolean;
};

export const ListCardContent: React.FC<Props> = props => {
  const {
    name,
    highlightedName,
    rank,
    rating,
    reviewsCount,
    diffPercentage = 0,
    price,
    priceInclShipping,
    properties,
    showFromPrice,
    store,
  } = props;

  const { t } = useAppContext();

  const prettyProperties = properties?.length
    ? properties
        .filter(p => p.isCore)
        .map(p => p.pretty)
        .filter(x => x)
    : [];

  return (
    <MainContent>
      <ProductName>{highlightedName || name}</ProductName>

      <BodySmallText emphasis={FontEmphasis.MEDIUM}>
        {prettyProperties.join(', ')}
      </BodySmallText>

      <RowWrapper>
        {rank && rank > 0 && (
          <Tag>
            <CaptionText emphasis={FontEmphasis.MEDIUM}>
              {t('product', 'rank.position', {
                rank,
              })}
            </CaptionText>
          </Tag>
        )}

        {rating ? (
          <>
            <MediumUp>
              <Rating
                rate={rating}
                count={reviewsCount}
                showCounter
                showCounterAs="number"
                showRate
                showRateAs="number"
              />
            </MediumUp>

            <MobileOnly>
              <Rating
                rate={rating}
                count={reviewsCount}
                showCounter
                showCounterAs="number"
                showRate
                showRateAs="number"
                singleStar
              />
            </MobileOnly>
          </>
        ) : null}
      </RowWrapper>

      <RowWrapper>
        {diffPercentage ? (
          <DealBadge diffPercentage={diffPercentage} position="relative" />
        ) : null}

        {price && (
          <PriceTextWrapper gap={0.5}>
            {showFromPrice && (
              <>
                <BodySmallText emphasis={FontEmphasis.LOW}>
                  {t('product', 'price.from-abbr')}
                </BodySmallText>{' '}
              </>
            )}
            <Price
              price={price}
              isDeal={Boolean(diffPercentage)}
              currency={store?.currency}
            />
            {Boolean(priceInclShipping) && (
              <BodySmallText emphasis={FontEmphasis.LOW}>
                {t('product', 'price.including-shipping', {
                  price: <Currency price={priceInclShipping} />,
                })}
              </BodySmallText>
            )}
          </PriceTextWrapper>
        )}
      </RowWrapper>
    </MainContent>
  );
};
